import { FONT_QUICKSAND, INK, MARIGOLD, PAPER } from '../tokens'

/**
 * The floating STEM-symbol / Indian-script texture already used on the b2c
 * marketing hero (π, ∞, कम, தொ, ਜਉ) -- carried into the product as a real,
 * reusable component instead of one-off copy-pasted spans per screen.
 * Render inside a `position: relative` night-colored surface (e.g. a
 * TornCard's `stage` slot).
 */
export function AmbientDoodles() {
  const items: { content: string; top: string; left?: string; right?: string; fontSize: string; opacity: number }[] = [
    { content: 'π', top: '8%', left: '6%', fontSize: '1.6rem', opacity: 0.16 },
    { content: 'क', top: '38%', left: '3%', fontSize: '1.2rem', opacity: 0.14 },
    { content: '∫f(x)dx', top: '6%', left: '38%', fontSize: '1.1rem', opacity: 0.15 },
    { content: 'தமி', top: '4%', right: '22%', fontSize: '1.4rem', opacity: 0.15 },
    { content: 'a²+b²=c²', top: '34%', right: '8%', fontSize: '1.3rem', opacity: 0.14 },
    { content: 'ਪੰ', top: '14%', right: '6%', fontSize: '1.1rem', opacity: 0.13 },
    { content: '÷', top: '46%', left: '44%', fontSize: '1rem', opacity: 0.13 },
  ]
  return (
    <>
      {items.map((it, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: it.top,
            left: it.left,
            right: it.right,
            fontFamily: FONT_QUICKSAND,
            fontWeight: 700,
            color: '#fff',
            fontSize: it.fontSize,
            opacity: it.opacity,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {it.content}
        </div>
      ))}
    </>
  )
}

/**
 * Doodles spilling through a TornCard's tear line -- same beat as objects
 * spilling out of a torn-open head on the Awwwards reference that inspired
 * this direction, translated to "knowledge spilling out" for Branon. Pass
 * as a TornCard's `spill` prop.
 */
export function SpillingDoodles() {
  const shadow = 'drop-shadow(0 4px 0 rgba(0,0,0,.12))'
  return (
    <>
      <svg
        aria-hidden="true"
        style={{ position: 'absolute', bottom: -40, left: '50%', filter: shadow }}
        width="86"
        height="86"
        viewBox="0 0 24 24"
        fill="none"
        stroke={MARIGOLD}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(-60 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4.5" />
        <circle cx="12" cy="12" r="1.6" fill={MARIGOLD} />
      </svg>
      <svg
        aria-hidden="true"
        style={{ position: 'absolute', bottom: -32, left: '63%', transform: 'rotate(-10deg)', filter: shadow }}
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#FF6B4A"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 3h6M10 3v5.5L4.5 18a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 8.5V3" />
      </svg>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -30,
          left: '75%',
          fontFamily: FONT_QUICKSAND,
          fontWeight: 700,
          color: '#7BE6C7',
          fontSize: '2.6rem',
          transform: 'rotate(6deg)',
          filter: shadow,
        }}
      >
        अ आ
      </div>
      <svg
        aria-hidden="true"
        style={{ position: 'absolute', bottom: -34, left: '12%', transform: 'rotate(10deg)', filter: shadow }}
        width="58"
        height="58"
        viewBox="0 0 24 24"
        fill={PAPER}
        stroke={INK}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <line x1="8" y1="7" x2="16" y2="7" />
        <line x1="8" y1="11" x2="16" y2="11" />
        <line x1="8" y1="15" x2="13" y2="15" />
      </svg>
    </>
  )
}

/**
 * A smaller-scale spill for `TopTearCard`'s short band -- same "knowledge
 * spilling out" beat as `SpillingDoodles`, sized down (~55%) and reduced to
 * two elements so it reads as a light accent rather than clutter on a card
 * that's already carrying an icon, title, and subtitle. Pass as
 * `TopTearCard`'s `spill` prop.
 */
export function SpillingDoodlesCompact() {
  const shadow = 'drop-shadow(0 3px 0 rgba(0,0,0,.12))'
  return (
    <>
      <svg
        aria-hidden="true"
        style={{ position: 'absolute', bottom: -20, left: '14%', filter: shadow }}
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke={MARIGOLD}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(-60 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4.5" />
        <circle cx="12" cy="12" r="1.6" fill={MARIGOLD} />
      </svg>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -16,
          left: '82%',
          fontFamily: FONT_QUICKSAND,
          fontWeight: 700,
          color: '#7BE6C7',
          fontSize: '1.5rem',
          transform: 'rotate(6deg)',
          filter: shadow,
        }}
      >
        அ
      </div>
    </>
  )
}
